/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { PhoneNumber } from '../models/PhoneNumber';
import type { PhoneNumberDaten } from '../models/PhoneNumberDaten';
import type { PhoneNumberListe } from '../models/PhoneNumberListe';
import type { PhoneNumberPromptDaten } from '../models/PhoneNumberPromptDaten';
import type { Prompt } from '../models/Prompt';
import type { PromptListe } from '../models/PromptListe';
import type { ToolConfig } from '../models/ToolConfig';
import type { ToolConfigDaten } from '../models/ToolConfigDaten';
import type { ToolConfigListe } from '../models/ToolConfigListe';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class PhonenumbersService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Konfigurationen eines Tools für eine Telefonnummer
   * Listet alle Konfigurationen eines Tools für eine Telefonnummer
   * @returns ToolConfigListe Liste der Konfigurationen
   * @throws ApiError
   */
  public getToolConfigsForPhoneNumber({
    phoneNumberId,
    toolId,
  }: {
    phoneNumberId: string,
    toolId: string,
  }): CancelablePromise<ToolConfigListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/phonenumbers/{phoneNumberId}/tools/{toolId}/configs',
      path: {
        'phoneNumberId': phoneNumberId,
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool-Konfiguration für Telefonnummer erstellen/aktualisieren
   * Erstellt oder aktualisiert einen Konfigurationswert eines Tools für eine Telefonnummer
   * @returns ToolConfig Die gespeicherte Konfiguration
   * @throws ApiError
   */
  public upsertPhoneNumberToolConfig({
    phoneNumberId,
    toolId,
    key,
    requestBody,
  }: {
    phoneNumberId: string,
    toolId: string,
    key: string,
    requestBody: ToolConfigDaten,
  }): CancelablePromise<ToolConfig> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/phonenumbers/{phoneNumberId}/tools/{toolId}/configs/{key}',
      path: {
        'phoneNumberId': phoneNumberId,
        'toolId': toolId,
        'key': key,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool-Konfiguration für Telefonnummer löschen
   * Löscht eine Konfiguration eines Tools für eine Telefonnummer
   * @returns ToolConfig Die gelöschte Konfiguration
   * @throws ApiError
   */
  public deletePhoneNumberToolConfig({
    phoneNumberId,
    toolId,
    key,
  }: {
    phoneNumberId: string,
    toolId: string,
    key: string,
  }): CancelablePromise<ToolConfig> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/phonenumbers/{phoneNumberId}/tools/{toolId}/configs/{key}',
      path: {
        'phoneNumberId': phoneNumberId,
        'toolId': toolId,
        'key': key,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Telefonnummern auflisten
   * Liefert alle Telefonnummern, sortiert nach Nummer aufsteigend.
   * @returns PhoneNumberListe Liste aller Telefonnummern
   * @throws ApiError
   */
  public listPhoneNumbers(): CancelablePromise<PhoneNumberListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/phonenumbers',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Telefonnummer anlegen
   * Legt eine neue Telefonnummer an.
   * @returns PhoneNumber Die neu angelegte Telefonnummer
   * @throws ApiError
   */
  public createPhoneNumber({
    requestBody,
  }: {
    requestBody: PhoneNumberDaten,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/phonenumbers',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Eingabe (z. B. leere Nummer oder Nummer bereits vorhanden).`,
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * Eine Telefonnummer bekommen
   * Liefert eine einzelne Telefonnummer anhand ihrer ID.
   * @returns PhoneNumber Die Telefonnummer
   * @throws ApiError
   */
  public getPhoneNumber({
    phoneNumberId,
  }: {
    /**
     * Eindeutige ID der Telefonnummer
     */
    phoneNumberId: string,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/phonenumbers/{phoneNumberId}',
      path: {
        'phoneNumberId': phoneNumberId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Telefonnummer aktualisieren
   * Aktualisiert die Inbound-Konfiguration einer Telefonnummer.
   * @returns PhoneNumber Die aktualisierte Telefonnummer
   * @throws ApiError
   */
  public updatePhoneNumber({
    phoneNumberId,
    requestBody,
  }: {
    /**
     * Eindeutige ID der Telefonnummer
     */
    phoneNumberId: string,
    requestBody: PhoneNumberDaten,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/phonenumbers/{phoneNumberId}',
      path: {
        'phoneNumberId': phoneNumberId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Ungültige Eingabe.`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Telefonnummer löschen
   * Löscht (soft-delete) eine Telefonnummer.
   * @returns PhoneNumber Die gelöschte Telefonnummer
   * @throws ApiError
   */
  public deletePhoneNumber({
    phoneNumberId,
  }: {
    /**
     * Eindeutige ID der Telefonnummer
     */
    phoneNumberId: string,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/phonenumbers/{phoneNumberId}',
      path: {
        'phoneNumberId': phoneNumberId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool zu Telefonnummer hinzufügen
   * Fügt ein Tool zu einer Telefonnummer hinzu (globale Tools sowie Tools der Mandanten dieser Telefonnummer; wird für den Inbound-Fallback ohne Aufgabe verwendet)
   * @returns PhoneNumber Die aktualisierte Telefonnummer
   * @throws ApiError
   */
  public addToolToPhoneNumber({
    phoneNumberId,
    toolId,
  }: {
    phoneNumberId: string,
    toolId: string,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/phonenumbers/{phoneNumberId}/tools/{toolId}',
      path: {
        'phoneNumberId': phoneNumberId,
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * Tool von Telefonnummer entfernen
   * Entfernt ein Tool von einer Telefonnummer
   * @returns PhoneNumber Die aktualisierte Telefonnummer
   * @throws ApiError
   */
  public removeToolFromPhoneNumber({
    phoneNumberId,
    toolId,
  }: {
    phoneNumberId: string,
    toolId: string,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/phonenumbers/{phoneNumberId}/tools/{toolId}',
      path: {
        'phoneNumberId': phoneNumberId,
        'toolId': toolId,
      },
      errors: {
        404: `Not Found`,
      },
    });
  }

  /**
   * ICP-Fallback-Prompt-Versionen einer Telefonnummer
   * Liefert alle ICP-Fallback-Prompt-Versionen einer Telefonnummer, sortiert nach Erstellzeitpunkt absteigend (neueste zuerst).
   * @returns PromptListe Liste aller ICP-Fallback-Prompt-Versionen für die Telefonnummer
   * @throws ApiError
   */
  public getIcpFallbackPromptsByPhoneNumberId({
    phoneNumberId,
  }: {
    /**
     * Eindeutige ID der Telefonnummer
     */
    phoneNumberId: string,
  }): CancelablePromise<PromptListe> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/phonenumbers/{phoneNumberId}/icp-fallback-prompts',
      path: {
        'phoneNumberId': phoneNumberId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Neue ICP-Fallback-Prompt-Version anlegen
   * Erstellt eine neue ICP-Fallback-Prompt-Version für die Telefonnummer. `prevVersion`
   * der neuen Version wird serverseitig auf die aktuell aktive Prompt-Version gesetzt.
   * Standardmäßig wird die neue Version anschließend als aktive Version der Telefonnummer
   * übernommen (kann mit `?activate=false` deaktiviert werden).
   *
   * @returns Prompt Die neu erstellte Prompt-Version
   * @throws ApiError
   */
  public createIcpFallbackPrompt({
    phoneNumberId,
    requestBody,
    activate = true,
  }: {
    phoneNumberId: string,
    requestBody: PhoneNumberPromptDaten,
    /**
     * Wenn true (Standard), wird die neue Version direkt als aktive Version der Telefonnummer gesetzt.
     */
    activate?: boolean,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/api/v1/phonenumbers/{phoneNumberId}/icp-fallback-prompts',
      path: {
        'phoneNumberId': phoneNumberId,
      },
      query: {
        'activate': activate,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Aktive ICP-Fallback-Prompt-Version einer Telefonnummer
   * Liefert die aktuell aktive ICP-Fallback-Prompt-Version einer Telefonnummer.
   * @returns Prompt Die aktuell aktive ICP-Fallback-Prompt-Version
   * @throws ApiError
   */
  public getActiveIcpFallbackPrompt({
    phoneNumberId,
  }: {
    /**
     * Eindeutige ID der Telefonnummer
     */
    phoneNumberId: string,
  }): CancelablePromise<Prompt> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/api/v1/phonenumbers/{phoneNumberId}/active-icp-fallback-prompt',
      path: {
        'phoneNumberId': phoneNumberId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * Aktive ICP-Fallback-Prompt-Version einer Telefonnummer setzen
   * Setzt eine andere ICP-Fallback-Prompt-Version als aktive Version der Telefonnummer.
   * Die Prompt-Version muss bereits zur Telefonnummer gehören (über `prompt_relation`).
   *
   * @returns PhoneNumber Die aktualisierte Telefonnummer
   * @throws ApiError
   */
  public setActiveIcpFallbackPrompt({
    phoneNumberId,
    promptId,
  }: {
    phoneNumberId: string,
    promptId: string,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/api/v1/phonenumbers/{phoneNumberId}/active-icp-fallback-prompt/{promptId}',
      path: {
        'phoneNumberId': phoneNumberId,
        'promptId': promptId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * ICP-Fallback-Prompt-Version einer Telefonnummer deaktivieren
   * Entfernt die aktive ICP-Fallback-Prompt-Version von der Telefonnummer.
   * Eingehende Anrufe verwenden dann die Prompts der jeweiligen Aufgaben.
   * Der `promptId` Pfadparameter wird ignoriert; übergeben Sie einen beliebigen Wert.
   *
   * @returns PhoneNumber Die aktualisierte Telefonnummer
   * @throws ApiError
   */
  public deactivateIcpFallbackPrompt({
    phoneNumberId,
    promptId,
  }: {
    phoneNumberId: string,
    promptId: string,
  }): CancelablePromise<PhoneNumber> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/api/v1/phonenumbers/{phoneNumberId}/active-icp-fallback-prompt/{promptId}',
      path: {
        'phoneNumberId': phoneNumberId,
        'promptId': promptId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

}
